import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { Plus } from "lucide-react";

import { AssessmentsDocument, ChoiceSet } from "./assessmentsTypes";
import styles from "./CompanyAssessments.module.css";
import { addChoiceSet } from "./assessmentsMutations";

interface ChoiceSetListModalProps {
    open: boolean;
    document: AssessmentsDocument;
    onOpenChange: (open: boolean) => void;
    onSelect: (choiceSet: ChoiceSet) => void;
    onDraftChange: (document: AssessmentsDocument) => void;
}

export function ChoiceSetListModal({
    open,
    document,
    onOpenChange,
    onSelect,
    onDraftChange
}: ChoiceSetListModalProps) {
    const { t } = useTranslation();

    const handleCreate = () => {
        const { document: next, choiceSet } = addChoiceSet(document);
        onDraftChange(next);
        onSelect(choiceSet);
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className={styles.dialogOverlay} />
                <Dialog.Content className={styles.dialogContent}>
                    <Dialog.Title className={styles.dialogTitle}>
                        {t("settingsPage.company.assessments.createMultipleChoice")}
                    </Dialog.Title>
                    <Dialog.Description className={styles.dialogDescription}>
                        {t("settingsPage.company.assessments.createMultipleChoiceDescription")}
                    </Dialog.Description>

                    {document.choice_sets.length === 0 ? (
                        <p className={styles.emptyState}>{t("settingsPage.company.assessments.noChoiceSets")}</p>
                    ) : (
                        <div className={styles.list}>
                            {document.choice_sets.map(choiceSet => (
                                <button
                                    key={choiceSet.id}
                                    type="button"
                                    className={styles.listItem}
                                    onClick={() => onSelect(choiceSet)}
                                >
                                    <span className={styles.listItemTitle}>
                                        {choiceSet.title?.trim() ||
                                            t("settingsPage.company.assessments.untitledChoiceSet")}
                                    </span>
                                    <span className={styles.listItemMeta}>
                                        {t("settingsPage.company.assessments.choiceCount", {
                                            count: choiceSet.choices.length
                                        })}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    <div className={styles.dialogActions}>
                        <div />
                        <button type="button" className={styles.dialogAddButton} onClick={handleCreate}>
                            <Plus size={14} />
                            {t("settingsPage.company.assessments.createChoiceSet")}
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
